'use client'

import PriceWithDiscount from '@repo/ui/components/price-with-discount'
import { cn } from '@repo/ui/lib/utils'
import { LinkProps } from 'next/dist/client/link'
import Link from 'next/link'
import {
  AnchorHTMLAttributes,
  Children,
  ComponentPropsWithoutRef,
  CSSProperties,
  ElementType,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './carousel'

interface GridItemCarouselProps extends ComponentPropsWithoutRef<'div'> {
  tag?: ReactNode
}

export const ProductGridItemCarousel: FunctionComponent<PropsWithChildren<GridItemCarouselProps>> = ({ children, tag, ...rest }) => {
  const [api, setApi] = useState<CarouselApi>()
  const [scrollProgress, setScrollProgress] = useState(0)
  const childrenCount = Children.count(children)

  const onScroll = useCallback((embla: NonNullable<CarouselApi>) => {
    const progress = Math.max(0, Math.min(1, embla.scrollProgress()))

    setScrollProgress(progress * 100)
  }, [])

  useEffect(() => {
    if (api) {
      onScroll(api)
      api.on('reInit', onScroll).on('scroll', onScroll).on('slideFocus', onScroll)
    }
  }, [api, onScroll])

  if (childrenCount === 0) {
    return null
  }

  return (
    <div className='group/parent relative' {...rest}>
      <Carousel setApi={setApi}>
        <CarouselContent>{children}</CarouselContent>
        {childrenCount > 1 && (
          <>
            <div
              aria-hidden='true'
              style={{ '--count': childrenCount, '--current': `${scrollProgress}%` } as CSSProperties}
              className='left-(--current) absolute bottom-0 h-px w-[calc(100%_/_var(--count))] overflow-hidden bg-current transition-[opacity,transform] ease-out lg:opacity-0 lg:group-hover/item:opacity-100 lg:group-focus-visible/item:opacity-100'
            />
            <CarouselPrevious className='!opacity-0 lg:transition-opacity lg:ease-out lg:focus-visible:enabled:!opacity-100 lg:group-focus-within/parent:enabled:!opacity-100 lg:group-hover/item:enabled:!opacity-100' />
            <CarouselNext className='!opacity-0 lg:transition-opacity lg:ease-out lg:focus-visible:enabled:!opacity-100 lg:group-focus-within/parent:enabled:!opacity-100 lg:group-hover/item:enabled:!opacity-100' />
          </>
        )}
      </Carousel>
      {tag}
    </div>
  )
}

interface ProductGridItemCarouselItemProps {
  linkProps: LinkProps & AnchorHTMLAttributes<HTMLAnchorElement>
  className?: string
}

export const ProductGridItemCarouselItem: FunctionComponent<PropsWithChildren<ProductGridItemCarouselItemProps>> = ({ children, linkProps, className }) => {
  return (
    <CarouselItem className={cn('relative aspect-[2/3] w-full', className)} asChild={true}>
      <Link {...linkProps}>{children}</Link>
    </CarouselItem>
  )
}

interface ProductGridItemRootProps extends ComponentPropsWithoutRef<'li'> {
  className?: string
}

export const ProductGridItemRoot: FunctionComponent<PropsWithChildren<ProductGridItemRootProps>> = ({ children, className, ...rest }) => {
  return (
    <li>
      <article className={cn('group/item flex flex-col', className)} {...rest}>
        {children}
      </article>
    </li>
  )
}

interface ProductGridItemTagProps extends ComponentPropsWithoutRef<'span'> {
  className?: string
}

export const ProductGridItemTag: FunctionComponent<PropsWithChildren<ProductGridItemTagProps>> = ({ children, className, ...rest }) => {
  if (!children) {
    return null
  }

  return (
    <span
      className={cn(
        'left-miista-grid-item-padding-mobile-x top-miista-grid-item-padding-mobile-x lg:left-miista-grid-item-padding-desktop-x lg:top-miista-grid-item-padding-desktop-y absolute text-sm uppercase transition-opacity ease-out group-focus-within/parent:opacity-100 group-hover/item:opacity-100 group-focus-visible:opacity-100 lg:opacity-0',
        className,
      )}
      role='note'
      {...rest}
    >
      {children}
    </span>
  )
}

interface ProductGridItemInfoProps extends ComponentPropsWithoutRef<'div'> {
  title?: string
  linkProps: LinkProps & AnchorHTMLAttributes<HTMLAnchorElement>
  price?: number
  discount?: number
  currencyCode?: string
  className?: string
  wishlist?: ReactNode
  titleAs?: ElementType
}

export const ProductGridItemInfo: FunctionComponent<PropsWithChildren<ProductGridItemInfoProps>> = ({
  children,
  title,
  linkProps,
  price,
  discount = 0,
  currencyCode = 'EUR',
  className,
  wishlist,
  titleAs = 'h2',
  ...rest
}) => {
  const TitleType = titleAs

  return (
    <div
      className={cn(
        'px-miista-grid-item-padding-mobile-x py-miista-grid-item-padding-mobile-y lg:px-miista-grid-item-padding-desktop-x lg:py-miista-grid-item-padding-desktop-y flex flex-nowrap justify-between',
        className,
      )}
      {...rest}
    >
      <div className='flex w-10/12 flex-1 flex-col gap-1 text-sm uppercase'>
        <TitleType className='truncate'>
          <Link className='outline-hidden no-underline focus-visible:font-semibold' {...linkProps}>
            {title}
          </Link>
        </TitleType>
        {price && <PriceWithDiscount className='flex items-center gap-[inherit]' amount={price} discount={discount} currencyCode={currencyCode} />}
        {!!children && <div className='h-5 overflow-hidden pt-0.5'>{children}</div>}
      </div>
      {wishlist}
    </div>
  )
}

type Hex = Readonly<string>

interface ProductWithColor {
  color: Hex
  linkProps: LinkProps & AnchorHTMLAttributes<HTMLAnchorElement>
}

interface ProductGridItemSwatchesProps extends ComponentPropsWithoutRef<'ul'> {
  current: ProductWithColor
  others?: ProductWithColor[]
  className?: string
  label?: string
  limit?: number
}

export const ProductGridItemSwatches: FunctionComponent<ProductGridItemSwatchesProps> = ({ current, others = [], className, label, limit = 3, ...rest }) => {
  const products = [current, ...others]

  if (products.length === 0) {
    return null
  }

  return (
    <ul
      className={cn('flex gap-1 leading-none ease-out lg:opacity-0 lg:transition-opacity lg:group-hover/item:opacity-100 lg:group-focus-visible/info:opacity-100', className)}
      aria-label={label?.replace('%1', products.length.toString())}
      {...rest}
    >
      {products.slice(0, limit).map((product, i) => {
        return (
          <li key={i} className='inline-flex'>
            <Link className='bg-(--color) block size-2 rounded-full' style={{ '--color': product.color } as CSSProperties} {...product.linkProps}>
              <span className='sr-only'>{product.linkProps?.title || product.color}</span>
            </Link>
          </li>
        )
      })}
      {products.length > limit && <li className='relative top-px'>+{products.length - limit}</li>}
    </ul>
  )
}
